/**
 * Job Career Services Section
 * Handles career services for all job categories:
 * - Technology & IT
 * - Office & Admin
 * - Customer Service
 * - Retail
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Star,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Sparkles,
  Target,
  FileText,
  MessageSquare,
  GraduationCap,
  Building2,
  Phone,
  Mail,
  Briefcase,
  Users,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useCountry } from '../../context/CountryContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency config
const CURRENCY_CONFIG = {
  UK: { code: 'GBP', symbol: '£' },
  SL: { code: 'LKR', symbol: 'Rs' },
};

// Color mappings for service cards
const COLOR_CLASSES = {
  blue: {
    border: 'border-l-blue-500',
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-700',
  },
  purple: {
    border: 'border-l-purple-500',
    badge: 'bg-purple-50 text-purple-700 border-purple-200',
    iconBg: 'bg-purple-100',
    iconText: 'text-purple-600',
    button: 'bg-purple-600 hover:bg-purple-700',
  },
  emerald: {
    border: 'border-l-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
    button: 'bg-emerald-600 hover:bg-emerald-700',
  },
  amber: {
    border: 'border-l-amber-500',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    button: 'bg-amber-600 hover:bg-amber-700',
  },
};

// Supported job categories for services
const SUPPORTED_CATEGORIES = [
  'technology-it', 
  'office-admin', 
  'customer-service', 
  'retail',
  'finance-accounting',
  'healthcare-care',
  'driver-delivery',
  'skilled-trades',
  'education-training',
  'security',
  'cleaning-domestic',
  'sales-marketing',
  'customer-success',
  'hr-jobs',
  'hospitality',
  'hospitality-tourism',  // Alias for hospitality
  'construction-property',
  'construction',  // Alias for construction-property
  'manufacturing-warehouse',
  'warehouse',  // Alias for manufacturing-warehouse
  'manufacturing',  // Alias for manufacturing-warehouse
  'creative-media',
  'creative',  // Alias for creative-media
  'media',  // Alias for creative-media
  'logistics-transport',
  'logistics',  // Alias for logistics-transport
  'transport',  // Alias for logistics-transport
  'driver',  // Alias for logistics-transport
  'delivery',  // Alias for logistics-transport
  'freelance-gigs',  // Freelance & Gigs category
  'freelance',  // Alias for freelance-gigs
  'gigs',  // Alias for freelance-gigs
  'gig-economy',  // Alias for freelance-gigs
];


// ==================== GENERIC SERVICE CARD ====================
const JobServiceCard = ({ service, currency, jobCategory, country }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    target_role: '',
    notes: '',
    scheduled_date: '',
    scheduled_time: '',
  });
  const { toast } = useToast();

  const colors = COLOR_CLASSES[service.color] || COLOR_CLASSES.blue;
  const isEmployerService = service.category === 'employer';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isEmployerService 
        ? `${API_URL}/api/job-services/employer-screening`
        : `${API_URL}/api/job-services/bookings`;
      
      const payload = isEmployerService
        ? {
            service_type: service.id,
            job_category: jobCategory,
            employer_name: form.customer_name,
            employer_email: form.customer_email,
            employer_phone: form.customer_phone,
            company_name: form.company_name || '',
            job_title: form.target_role,
            candidates_count: parseInt(form.candidates_count) || 1,
            notes: form.notes,
          }
        : {
            service_type: service.id,
            job_category: jobCategory,
            customer_name: form.customer_name,
            customer_email: form.customer_email,
            customer_phone: form.customer_phone,
            target_role: form.target_role,
            notes: form.notes,
            scheduled_date: form.scheduled_date,
            scheduled_time: form.scheduled_time,
          };
      
      const response = await axios.post(endpoint, payload, { params: { country } });
      
      if (response.data.success) {
        toast({
          title: 'Request Submitted!',
          description: `Reference: ${response.data.booking_id}`,
        });
        setShowDialog(false);
        setForm({
          customer_name: '',
          customer_email: '',
          customer_phone: '',
          target_role: '',
          notes: '',
          scheduled_date: '',
          scheduled_time: '',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to submit request',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card 
        className={`relative overflow-hidden border-l-4 ${colors.border} hover:shadow-lg transition-all cursor-pointer`}
        data-testid={`service-card-${service.id}`}
      >
        <CardContent className="p-4">
          {/* Badge */}
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className={`text-xs ${colors.badge}`}>
              <Sparkles className="h-3 w-3 mr-1" />
              {service.badge_type}
            </Badge>
          </div>

          {/* Title */}
          <div className="flex items-start gap-3 mb-3">
            <div className={`p-2 ${colors.iconBg} rounded-lg`}>
              <span className="text-xl">{service.icon}</span>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">{service.name}</h3>
              <p className="text-sm text-gray-500">{service.subtitle}</p>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-1 mb-3">
            {service.features?.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                {feature}
              </div>
            ))}
          </div>

          {/* Rating & Price */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-sm">
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              <span className="font-medium">4.8</span>
              <span className="text-gray-400">· Online</span>
            </div>
            <div className="text-right">
              <span className="text-lg font-bold text-gray-900">
                From {currency?.symbol || '£'}{service.price}
              </span>
              {service.billing_period && (
                <span className="text-sm text-gray-500"> / {service.billing_period}</span>
              )}
            </div>
          </div>

          {/* CTA Button */}
          <Button 
            className={`w-full mt-3 ${colors.button}`}
            onClick={(e) => {
              e.stopPropagation();
              setShowDialog(true);
            }}
            data-testid={`service-cta-${service.id}`}
          >
            {service.cta_text || 'Get Started'}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </CardContent>
      </Card>

      {/* Booking Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{service.name}</DialogTitle>
            <DialogDescription>
              {isEmployerService 
                ? 'Submit your screening request'
                : 'Fill in your details to get started'
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">{isEmployerService ? 'Your Name' : 'Full Name'} *</Label>
              <Input
                id="name"
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                placeholder="Your name"
                required
              />
            </div>
            
            {isEmployerService && (
              <div>
                <Label htmlFor="company">Company Name *</Label>
                <Input
                  id="company"
                  value={form.company_name || ''}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="Your company"
                  required
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={form.customer_email}
                onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                placeholder="your@email.com"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                value={form.customer_phone}
                onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                placeholder="+94 7XX XXX XXXX"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="role">{isEmployerService ? 'Job Title' : 'Target Role'}</Label>
              <Input
                id="role"
                value={form.target_role}
                onChange={(e) => setForm({ ...form, target_role: e.target.value })}
                placeholder={isEmployerService ? 'e.g., Office Administrator' : 'e.g., Executive Assistant'}
              />
            </div>
            
            {isEmployerService && (
              <div>
                <Label htmlFor="candidates">Number of Candidates</Label>
                <Input
                  id="candidates"
                  type="number"
                  min="1"
                  value={form.candidates_count || 1}
                  onChange={(e) => setForm({ ...form, candidates_count: e.target.value })}
                />
              </div>
            )}
            
            {!isEmployerService && service.id === 'interview_prep' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">Preferred Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={form.scheduled_date}
                    onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="time">Preferred Time</Label>
                  <Input
                    id="time"
                    type="time"
                    value={form.scheduled_time}
                    onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })}
                  />
                </div>
              </div>
            )}
            
            <div>
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Any specific requirements..."
                rows={2}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className={colors.button}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};


// ==================== MAIN SERVICES SECTION ====================
const JobCareerServicesSection = ({ jobCategory }) => {
  const { country } = useCountry();
  const [services, setServices] = useState([]);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const currency = CURRENCY_CONFIG[country] || CURRENCY_CONFIG.UK;

  // Check if this category has services
  const hasServices = SUPPORTED_CATEGORIES.includes(jobCategory);

  useEffect(() => {
    const fetchServices = async () => {
      if (!hasServices) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/api/job-services/services/${jobCategory}`, {
          params: { country, service_category: 'candidate' }
        });
        
        if (response.data.success) {
          setServices(response.data.services || []);
          setCategoryInfo({
            name: response.data.category_name,
            icon: response.data.category_icon,
          });
        }
      } catch (error) {
        console.error('Error fetching job services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [jobCategory, country, hasServices]);

  if (!hasServices || loading) return null;
  if (services.length === 0) return null;

  return (
    <Card className="mb-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200" data-testid="job-services-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600" />
            {categoryInfo?.name} Career Services
          </CardTitle>
          <Badge variant="outline" className="bg-white">
            <Target className="h-3 w-3 mr-1" />
            Recommended for you
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.slice(0, 3).map((service) => (
            <JobServiceCard
              key={service.id}
              service={service}
              currency={currency}
              jobCategory={jobCategory}
              country={country}
            />
          ))}
        </div>
        {/* View Bundles Link */}
        <div className="mt-4 pt-4 border-t border-blue-100 flex justify-center">
          <Button 
            variant="outline" 
            className="bg-white hover:bg-blue-50"
            onClick={() => window.location.href = `/jobs?category=${jobCategory}&show_bundles=true`}
            data-testid="view-bundles-btn"
          >
            <Briefcase className="h-4 w-4 mr-2" />
            View Bundle Packages & Save
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


// ==================== TRIGGER PROMPT COMPONENT ====================
export const JobServiceTriggerPrompt = ({ jobCategory, triggerType = 'view_jobs' }) => {
  const { country } = useCountry();
  const [triggerData, setTriggerData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  
  const currency = CURRENCY_CONFIG[country] || CURRENCY_CONFIG.UK;
  const hasServices = SUPPORTED_CATEGORIES.includes(jobCategory);

  useEffect(() => {
    const fetchTrigger = async () => {
      if (!hasServices) return;
      
      try {
        const response = await axios.get(
          `${API_URL}/api/job-services/triggers/${jobCategory}/${triggerType}`,
          { params: { country } }
        );
        
        if (response.data.success && response.data.services?.length > 0) {
          setTriggerData(response.data);
        }
      } catch (error) {
        console.error('Error fetching trigger:', error);
      }
    };

    fetchTrigger();
  }, [jobCategory, triggerType, country, hasServices]);

  if (!hasServices || dismissed || !triggerData?.services?.length) return null;

  const primaryService = triggerData.services[0];
  const colors = COLOR_CLASSES[primaryService.color] || COLOR_CLASSES.blue;

  return (
    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 mt-6" data-testid="service-trigger-prompt">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="font-medium text-gray-900 mb-2">
              {triggerData.trigger?.message || 'Boost your chances'}
            </p>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
              <span className="text-2xl">{primaryService.icon}</span>
              <div className="flex-1">
                <p className="font-medium">{primaryService.name}</p>
                <p className="text-sm text-gray-500">
                  From {currency.symbol}{primaryService.price} · Online
                </p>
              </div>
              <Button size="sm" className={colors.button}>
                {primaryService.cta_text || 'Get Started'}
              </Button>
            </div>
          </div>
          <button 
            onClick={() => setDismissed(true)}
            className="text-gray-400 hover:text-gray-600 ml-2"
          >
            ×
          </button>
        </div>
      </CardContent>
    </Card>
  );
};


export default JobCareerServicesSection;
